import { useEffect, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Container,
  Group,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconSearch } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { MonitoringEntitlementControls, TeamNotificationSettings } from '@/components';
import { useTeams } from '@/hooks';
import { useMonitoringEntitlements } from '@/hooks/useMonitoringEntitlements';
import { vnextApiClient } from '@/services/vnext-api';
import { INTAKE_MODE_OPTIONS, IntakeMode } from '@/types/team-settings';

/** Rows per page of the client grid. */
const PAGE_SIZE = 25;

/**
 * Admin: per-client settings — one row per team.
 *
 * **Intake mode** shapes the Request Report page. Teams without an explicit
 * setting take the default (custom_configs — visible custom types if any,
 * otherwise enquiry-only). Edits save on change.
 *
 * **Monitoring** is the team's entitlement: whether its subjects may be
 * enrolled and watched. Its actions confirm first, because each changes what
 * Castellum bills the client for.
 *
 * **Notifications** opens a drawer for who is emailed about each report
 * notification; it is per team and loads only when opened.
 *
 * Intake and monitoring are separate backends (`/team-settings`,
 * `/monitoring/entitlements`) shown side by side, so a routine intake change can
 * never touch billing.
 *
 * The grid's rows are the teams lookup (Django) joined against both sets of
 * explicit rows, so the filter and the paging are client-side — the API holds
 * no team names to search.
 */
export function ClientSettingsPage() {
  const { teams } = useTeams();
  const { entitlements, saveDetails, change } = useMonitoringEntitlements();
  const [modes, setModes] = useState<Record<string, IntakeMode>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [notificationsTeam, setNotificationsTeam] = useState<{ id: string; name: string } | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    vnextApiClient
      .listTeamSettings()
      .then((rows) => {
        if (!cancelled) {
          setModes(Object.fromEntries(rows.map((r) => [r.teamId, r.intakeMode])));
        }
      })
      .catch((err) => Sentry.captureException(err));
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleTeams = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...teams].sort((a, b) => a.name.localeCompare(b.name));
    return q ? sorted.filter((t) => t.name.toLowerCase().includes(q)) : sorted;
  }, [teams, query]);
  const totalPages = Math.ceil(visibleTeams.length / PAGE_SIZE);
  const pageTeams = visibleTeams.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleChange = async (teamId: string, mode: IntakeMode) => {
    const previous = modes[teamId];
    setModes((m) => ({ ...m, [teamId]: mode }));
    setSaving(teamId);
    try {
      await vnextApiClient.putTeamSettings(teamId, mode);
    } catch (err) {
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
      setModes((m) => {
        const next = { ...m };
        if (previous === undefined) {
          delete next[teamId];
        } else {
          next[teamId] = previous;
        }
        return next;
      });
      notifications.show({
        title: 'Save failed',
        message: 'The intake mode was not changed. Please try again.',
        color: 'red',
      });
    } finally {
      setSaving(null);
    }
  };

  return (
    <Container size="lg" py="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Client settings</Title>
          <Text size="sm" c="dimmed">
            <Text component="span" fw={600}>
              Intake mode
            </Text>{' '}
            is what each client&apos;s Request Report page offers. Clients without an explicit
            setting use Custom report configs: their visible custom types if any — otherwise
            everything they submit raises an enquiry.{' '}
            <Text component="span" fw={600}>
              Monitoring
            </Text>{' '}
            is whether the client&apos;s subjects can be enrolled and watched.{' '}
            <Text component="span" fw={600}>
              Notifications
            </Text>{' '}
            is who is emailed about each report.
          </Text>
        </Stack>

        <TextInput
          placeholder="Filter clients…"
          leftSection={<IconSearch size={14} />}
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            setPage(1);
          }}
          maw={320}
          data-testid="client-intake-filter"
        />

        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Client</Table.Th>
              <Table.Th w={320}>Intake mode</Table.Th>
              <Table.Th w={180}>Monitoring</Table.Th>
              <Table.Th w={150}>Notifications</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pageTeams.map((team) => {
              const teamId = String(team.id);
              const explicit = modes[teamId];
              return (
                <Table.Tr key={teamId} data-testid={`intake-row-${teamId}`}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm">{team.name}</Text>
                      {explicit === undefined && (
                        <Badge size="xs" variant="outline" color="gray">
                          default
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      data={INTAKE_MODE_OPTIONS}
                      value={explicit ?? 'custom_configs'}
                      onChange={(value) => {
                        if (value) {
                          void handleChange(teamId, value as IntakeMode);
                        }
                      }}
                      disabled={saving === teamId}
                      allowDeselect={false}
                      data-testid={`intake-select-${teamId}`}
                    />
                  </Table.Td>
                  <Table.Td>
                    <MonitoringEntitlementControls
                      teamId={teamId}
                      teamName={team.name}
                      entitlement={entitlements[teamId]}
                      onSaveDetails={saveDetails}
                      onChange={change}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconBell size={14} />}
                      onClick={() => setNotificationsTeam({ id: teamId, name: team.name })}
                      aria-label={`Notification settings for ${team.name}`}
                      data-testid={`notifications-button-${teamId}`}
                    >
                      Notifications
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {totalPages > 1 ? (
          <Group justify="center">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        ) : null}
      </Stack>
      <TeamNotificationSettings
        teamId={notificationsTeam?.id ?? null}
        teamName={notificationsTeam?.name ?? ''}
        opened={notificationsTeam !== null}
        onClose={() => setNotificationsTeam(null)}
      />
    </Container>
  );
}
